/**
 * WHAT THE BUILD LEAVES BEHIND, AND IN WHAT SHAPE.
 *
 * The pipeline writes its output as versioned JSON files under `artifacts/`:
 *
 *   artifacts/bundle.json       the map, the graph, the manifests — loaded first
 *   artifacts/verses/v0.json    {"chunk":0,"eng":{...},"orig":{...},...}   one range of text
 *   artifacts/index/HB.json     one text's search postings
 *   artifacts/refs/HB.json      which passages name each figure
 *
 * The shapes below are the wire format as well as the file format. The server serves these
 * objects verbatim from the edge, and the client (`core/content.ts`) reads them straight
 * from there, so both type their bytes against THIS file rather than against a second
 * description of the same shape.
 *
 * The builders below assemble the JSON by hand, in a fixed key order, rather than handing an
 * object to `JSON.stringify` and trusting its insertion order. The order is part of the
 * bytes, and the bytes are hashed into the library version — a caller that passed the same
 * fields in a different order must not produce a different build, so the order is pinned
 * here where the writer lives.
 *
 * KEYS ARE STRINGS because JSON object keys are strings. A verse id is a number everywhere
 * else in this project; through one of these it is the same number written in decimal, and
 * typing it as `Record<number, …>` would be a claim JSON.parse cannot honour.
 */

/*
 * The one import this file has, and it earns it: `Grade` is the vocabulary a report is graded in,
 * declared beside `Report` where it is stored. Re-spelling the twelve terms here would be a second
 * description of one shape, which is what the paragraph above says this file exists to avoid.
 */
import type { Grade } from './reports.ts';

/**
 * One range of verse text: everything a passage displays, so opening one costs one fetch.
 *
 * `orig` is thinner than `eng` on purpose — a passage whose original is held back for want
 * of a licence still ships without it, which is what `mayPublishOriginal` decides.
 */
export interface VerseChunkArtifact {
  /** Which range this is. Ranges are cut on size; `Bundle.chunkStarts` says where each begins. */
  chunk: number;
  /** verse id -> the English a reader reads. */
  eng: Record<string, string>;
  /** verse id -> Hebrew, Greek, Aramaic or Arabic, where it may travel. */
  orig: Record<string, string>;
  /** verse id -> where the names in it fall, so the text can be linked without re-parsing. */
  mentions: Record<string, number[]>;
  /** verse id -> a second English translation, where the text carries one. */
  alt: Record<string, string>;
  /** verse id -> a third English — the Hebrew Bible's Jewish translation, and nothing else. */
  alt2: Record<string, string>;
  /**
   * translation id -> (verse id -> that translation's English), for the fourth and beyond.
   *
   * `alt` and `alt2` are three-slots thinking: a text gaining a Catholic English, or a second
   * rendering of the Quran, has nowhere to go. This is the same shape generalised, so how many
   * translations a verse carries becomes a fact about the data rather than about how many fields
   * somebody has added. The corpus names the keys; anything unnamed cannot be captioned and so
   * cannot be shown.
   *
   * Optional because artifacts written before it exists do not have it, and a reader on a stale
   * bundle should lose a translation rather than the page.
   */
  texts?: Record<string, Record<string, string>>;
  /**
   * verse id -> which collection a hadith comes from and what grade it carries, joined.
   *
   * One field rather than two because they are never shown apart, and a second sparse map
   * across 43,858 verses to serve 2,328 of them is payload for nothing. They remain separate
   * fields in `corpora/verses.json`, which is where anything reasoning about them reads.
   */
  prov: Record<string, string>;
}

/** One text's search postings: the words in it, and which passages hold each. */
export interface SearchIndexArtifact {
  corpus: string;
  /** word -> verse ids, capped per word. */
  index: Record<string, number[]>;
  /** word -> the TRUE number of passages, recorded only where the postings were cut. */
  df: Record<string, number>;
}

/** One text's name lists: which passages of it name each figure. */
export interface NodeRefsArtifact {
  corpus: string;
  /** node id -> verse ids. */
  refs: Record<string, number[]>;
}

/*
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 * THE COMPILATION, CUT INTO PIECES — `ARCHITECTURE.md D26`, designed in `DELIVERY.md §4`.
 *
 * Everything above is the LIBRARY: scripture, its indexes, its numbering. Public, anonymous, and
 * heavy. Everything below is the COMPILATION: what this project made. It moves behind the API and
 * is answered in pieces, and the pieces live under one prefix — `compilation/` — so that making
 * it private on the object store is one act rather than an audit (`DELIVERY.md §12`).
 *
 * WHY PIECES, given that one request would be simpler: because the whole layer is 348 kB gzipped
 * today and **6.4 MB by twenty thousand figures**, measured, with the edge count growing faster
 * than the figure count (`DELIVERY.md §4.7`). And because one request that yields everything is
 * exactly the exposure D26 exists to close — a login does not make it costly, only attributable.
 * `DELIVERY.md §4.9` prices the simpler option properly and says where it is right: the offline
 * download, where the reader asked for the whole thing.
 *
 * WHAT THE PIECES ARE, and which view each one serves:
 *
 *   map/overview.json   the zoomed-out view. One glyph per screen cell, not per figure — bounded
 *                       by the screen rather than the corpus, so it is ~7 kB at any size.
 *   map/skeleton.json   every figure the map draws, as rows. Correct while the corpus is small
 *                       and the thing to build first; it is the floor, not the design.
 *   node/<id>.json      what a click needs and the map never draws — the prose, the forms, the
 *                       provenance. Median 0.3 kB.
 * ═══════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * ONE ROW PER FIGURE, AND WHY IT IS A ROW.
 *
 * `[id, x, y, r, name, colour, kind, auto, corpora, contested, tier]`, in that order, matching
 * `SKELETON_COLUMNS` below. Measured against the alternatives (`DELIVERY.md §3`): rows beat keyed
 * objects 66 kB against 141 kB raw and 22.3 against 25.5 gzipped, and folding the position in beats
 * shipping `layout` as its own map by 10 kB gz because that repeats every id.
 *
 * The last five are pre-resolved so the client does no work the build could have done: `colour` is
 * an index into `Bundle.corpora` rather than the counts to derive it from, `corpora` is a bitmask
 * over the same order, `contested` is the one bit the map reads from `agreement`, and `tier` is the
 * rung the layout was asked to place the figure on.
 *
 * `tier` is on the row rather than derived from `x, y` because those are where the figure ENDED UP
 * after the crowd had its say, and the rung is where it was meant to be. It is also the reason this
 * row and `Bundle.layout` must gain the column together: the skeleton and the bundle are two doors
 * to the same renderer, and a `RenderNode` that differs by which door it came through is the drift
 * core exists to prevent — `map-model.test.ts` asserts they are identical.
 */
export type SkeletonRow = readonly [
  id: string,
  x: number,
  y: number,
  r: number,
  name: string,
  colour: number,
  kind: string,
  auto: 0 | 1,
  corpora: number,
  contested: 0 | 1,
  tier: number,
];

/** The row order, named so a reader of either side does not count commas. */
export const SKELETON_COLUMNS = ['id', 'x', 'y', 'r', 'name', 'colour', 'kind', 'auto', 'corpora', 'contested', 'tier'] as const;

/**
 * Every figure the map draws, and what it needs to draw them.
 *
 * NO VERSION FIELD, deliberately, and for the same reason none of the artifacts above carries one:
 * the version is in the PATH — `core/content.ts` puts it there precisely so it cannot be forgotten —
 * and a copy in the body would have to be folded into the library digest that is computed FROM these
 * bytes, which is a circle. A caller that asked for a version was given that version's bytes.
 */
export interface MapSkeletonArtifact {
  /** Corpus ids in the order `colour` and `corpora` index into. */
  corpora: string[];
  rows: SkeletonRow[];
  /** Edges as `[aIndex, bIndex, width×10]` against `rows`, sorted — 12.5 kB gz against 24 keyed. */
  edges: (readonly number[])[];
  /**
   * The scripture washes, carried straight through from `Bundle.fields`.
   *
   * Here as well as in the bundle because these are two doors to the same map, and a renderer must
   * not be able to tell which one it came through. A wash layer that appeared only on one door
   * would be exactly the drift `core/map-model.ts` exists to prevent — and it is a hundred-odd
   * numbers, so carrying it twice costs nothing worth measuring.
   */
  fields: Record<string, { x: number; y: number; n: number; r: number }[]>;
  /** The wedges and their labels, carried through from `Bundle.wheel` for the same reason. */
  wheel: { id: string; a: number; width: number; r: number; only: number }[];
}

/**
 * ONE GLYPH PER SCREEN CELL, for the view where a figure is two pixels.
 *
 * `[x, y, count, colour, maxR, contested]`. At the opening zoom nobody can read individual figures
 * — the renderer fades the edge web to a whisper precisely because the whole field at once is a grey
 * mesh — so shipping them individually buys a reader nothing. Cells are sized from the zoom and the
 * zoom from the world's extent, which is what makes this **bounded by the screen and not by the
 * corpus**: 526 cells at 1,099 figures and 561 at 100,000.
 *
 * The glyph sits at the CENTROID of the figures it stands for, never at the middle of its cell. That
 * is not a detail: rendered on a lattice it reads as a halftone screen in visible rows and columns
 * where the real map is an organic cluster, which the silhouette check in `DELIVERY.md §4.5` caught
 * and no byte count would have.
 */
export type OverviewCellRow = readonly [
  x: number,
  y: number,
  count: number,
  colour: number,
  maxR: number,
  contested: 0 | 1,
];

/**
 * ONE FIGURE, AS A TILE CARRIES IT.
 *
 * Positional like `SkeletonRow` and for the same reason: at ten thousand figures the keys cost more
 * than the values. The columns are ours rather than the reference engine's — a renderer converts —
 * because `corpora` here is the same INDEX MASK the skeleton uses and not a string of one-character
 * keys, and a mask is the shape the rest of this file already speaks.
 *
 * `id` is the figure's index into the skeleton's own rows, which is what makes a tie in a tile and
 * a tie in the skeleton the same tie. A tile that numbered its own figures would be a second
 * identity for every name in the library.
 */
export type TileRow = readonly [
  id: number,
  name: string,
  kind: string,
  /** Which scriptures name it, as a bitmask over `corpora`. */
  corpora: number,
  /** How many of them do — what the level of detail and the label budget read. */
  weight: number,
  radius: number,
  tier: number,
  contested: 0 | 1,
  auto: 0 | 1,
  x: number,
  y: number,
];

export const TILE_COLUMNS = ['id', 'name', 'kind', 'corpora', 'weight', 'radius', 'tier', 'contested', 'auto', 'x', 'y'] as const;

/** One tie inside a tile: two skeleton indices and a width, exactly as the skeleton carries them. */
export type TileEdgeRow = readonly [a: number, b: number, width: number];

/**
 * ONE TILE OF THE WORLD — the figures whose position falls inside it, and the ties between them.
 *
 * THE POINT IS THAT A READER FETCHES ONLY WHAT THEY ARE LOOKING AT. The whole field is one file
 * today because the library is small; the moment it is not, a map that ships every figure to
 * everybody is a map that takes seconds to open and megabytes to pan. Tiles are how the renderer
 * asks for a screenful, and how it can draw a region it has NOT fetched as a cloud whose brightness
 * is the count the index already told it.
 */
export interface MapTileArtifact {
  id: string;
  n: TileRow[];
  e: TileEdgeRow[];
}

/**
 * WHERE EVERY TILE IS AND HOW MANY FIGURES IT HOLDS.
 *
 * The count is the load-bearing part and it is easy to mistake for bookkeeping: it is what lets an
 * unfetched region be drawn as a cloud of about the right brightness instead of as a hole. Without
 * it the map would have to choose between a blank where the data is not yet and a fetch of
 * everything to find out whether anything is there.
 */
export interface MapTilesArtifact {
  /** Corpus ids in the order the tile rows' masks index into — the skeleton's order. */
  corpora: string[];
  /** How many figures the whole field holds, and how far out the naming reaches. */
  count: number;
  rim: number;
  /** World units per tile, on both axes. */
  tile: number;
  /** Every tile that holds anything, with its bounds and its count. */
  tiles: { id: string; x0: number; y0: number; x1: number; y1: number; n: number }[];
}

export interface MapOverviewArtifact {
  corpora: string[];
  cells: OverviewCellRow[];
  /**
   * `[fromCell, toCell, ties]` — and deliberately capped.
   *
   * Cells aggregate beautifully and links do not: at mean degree 7 nearly every pair of occupied
   * cells ends up joined, so 2,518 links become 104,778 by 100,000 figures. Capped at two per cell
   * the payload is flat at 7–9 kB, and what is dropped is a mesh nobody could see at this zoom.
   */
  links: (readonly number[])[];
}

/**
 * WHAT A CLICK NEEDS AND THE MAP NEVER DRAWS.
 *
 * `core/map-model.ts` is the authority on the split: the map reads id, name, kind, auto, corpora,
 * agreement and the counts it picks a colour from. Everything else in a gazetteer record is panel
 * and page material — and the largest part of it is `note`, **182 kB of curated prose across the
 * library**, which draws nothing.
 *
 * `counts` appears on both sides. The map needs it for a colour and the panel needs it to say how
 * often a figure is named, so it travels twice rather than being derived twice.
 */
export interface NodeRecordArtifact {
  /** The gazetteer record, less the fields the skeleton already carried. */
  node: Record<string, unknown>;
  /**
   * This figure's ties, as `[otherId, weight]` — the panel names them, so ids not indices.
   *
   * A THIRD ELEMENT WHERE THERE IS ONE: the earliest passage naming both. The weight says a tie
   * exists; the citation says where to read it, and the map writes it along the tie of a figure the
   * reader has opened. Absent rather than null when the library has none, because a positional row
   * of two or three is something every reader already copes with and a null is a byte to say
   * nothing three and a half thousand times.
   */
  ties: (readonly [string, number] | readonly [string, number, string])[];
}

/**
 * THE REST OF THE COMPILATION — the curated lists the panels and the generated pages read.
 *
 * The map has its skeleton and the click has its record; this is everything else `DELIVERY.md §2`
 * counts as ours. It exists as ONE artifact rather than ten because of who reads it: the page build
 * needs all of it at once, locally, to emit the question and parallel pages, and ten files to answer
 * one question is ten ways to be half-published.
 *
 * That is NOT how a reader gets it. The API serves these per view and paged — the contested list alone
 * was measured at 70% of a real session and is paged at 25 (`§4.5` item 5, `§5`) — and this artifact is
 * what the server pages FROM. One file on disk, many answers on the wire.
 *
 * Every field is optional and every one may be empty, because a library that has no parallels yet has
 * no parallels: absence must be sayable without a build refusing to publish an honest library.
 */
export interface CompilationListsArtifact {
  /** Passages measured as argued-over, ranked most-argued-over first by the build. */
  contestedPassages?: unknown[];
  /** Arguments that turn on passages, each naming the figures it is reached from. */
  passageDisputes?: unknown[];
  /** Figures whose cross-scripture identity is disputed, with the note that says how. */
  contested?: unknown[];
  /** Episodes told in both the Bible and the Quran. */
  stories?: unknown[];
  /** Where the accounts of one figure differ. */
  divergences?: unknown[];
  /** Chapters told twice within one corpus. */
  internalParallels?: unknown[];
  /** Verses the two numbering traditions place differently. */
  internal?: unknown[];
  /** What the manuscripts say about a verse the edition prints. */
  textualNotes?: Record<string, string>;
  /** Claims that several names are one being. */
  identities?: unknown[];
  /** How many reports name each figure, so a panel can say so without fetching them. */
  reportCounts?: Record<string, number>;
}

export const ARTIFACT_ROOT = 'artifacts';

/** The prefix every piece of the compilation lives under, so one rule makes all of it private. */
export const COMPILATION_PREFIX = 'compilation';

/**
 * WHAT A CORPUS ID MAY LOOK LIKE WHERE IT BECOMES PART OF A FILE NAME.
 *
 * A server builds `index/${corpus}.json` from a segment of a URL, so the id is attacker-
 * controlled by definition. Checking that the RESOLVED path stays inside the library is
 * necessary and is not sufficient: `../bundle` resolves to a file inside the library, so
 * containment lets it through, and the caller is handed the bundle where it asked for a
 * search index. That is not an escape, but it is one route answering for another, and a
 * check that cannot tell them apart will not tell anything else apart either.
 *
 * So an id is constrained to what an id actually is. The four in use — HB, NT, QURAN,
 * MISHNAH — and anything a text added later could reasonably be called.
 */
const ARTIFACT_ID = /^[A-Za-z0-9_-]+$/;

export function isArtifactId(id: string): boolean {
  return typeof id === 'string' && ARTIFACT_ID.test(id);
}

/**
 * ONE ARTIFACT'S BYTES, ASSEMBLED IN A PINNED KEY ORDER.
 *
 * Each returns the exact characters written to disk and served on the wire, trailing newline
 * and all. The key order is written out by hand — not left to `JSON.stringify`'s insertion
 * order — because the order is part of the bytes and the bytes are hashed into the version;
 * see the file header. Every string a caller supplies still goes through `JSON.stringify` so
 * a quote, a backslash or a `</script>` is escaped exactly as JSON requires.
 */
export function bundleJson(bundle: unknown): string {
  return `${JSON.stringify(bundle)}\n`;
}

/**
 * A range on its way to disk. `alt2` is optional HERE and required on the way back, which is
 * the actual asymmetry: a builder may not have a third translation to give — no range outside
 * the Hebrew Bible does — while a reader must never meet the field missing, because it is
 * always written. Modelled rather than cast away.
 */
export type VerseChunkInput = Omit<VerseChunkArtifact, 'alt2' | 'prov' | 'texts'>
  & {
    alt2?: Record<string, string>;
    prov?: Record<string, string>;
    /** One map per translation beyond the first three, keyed by the translation's id. */
    texts?: Record<string, Record<string, string>>;
  };

export function verseChunkJson(a: VerseChunkInput): string {
  const eng = JSON.stringify(a.eng);
  const orig = JSON.stringify(a.orig);
  const mentions = JSON.stringify(a.mentions);
  const alt = JSON.stringify(a.alt);
  /* `?? {}` because `JSON.stringify(undefined)` is the value undefined, not a string, and
     would splice the literal word into the file — which is what `artifacts.test.ts` means by
     "a consumer never sees undefined". The type requires the field; a JavaScript caller, or
     an artifact written before it existed, does not have to. */
  const alt2 = JSON.stringify(a.alt2 ?? {});
  const prov = JSON.stringify(a.prov ?? {});
  /* The fourth English and beyond. Empty in every chunk until a text declares one, so the
     artifact grows only where there is something to carry — and `?? {}` for the same reason
     `alt2` has it: a caller written before this existed must not splice `undefined` into a file. */
  const texts = JSON.stringify(a.texts ?? {});
  return `{"chunk":${a.chunk},"eng":${eng},"orig":${orig},"mentions":${mentions},"alt":${alt},"alt2":${alt2},"prov":${prov},"texts":${texts}}\n`;
}

/**
 * HADITH, FETCHED ON DEMAND FROM A FIGURE'S PANEL.
 *
 * Kept out of the bundle for the reason the verses are: 12,370 reports is 8.2 MB of Arabic,
 * and a reader who never opens a figure should not download it. The bundle carries only how
 * many reports each figure has; the text arrives when the panel is opened.
 *
 * One file per figure rather than fixed-size chunks. Verses are chunked because a reader
 * travels through them in reading order and a range is the natural unit; reports are reached
 * from ONE figure at a time and never read in sequence, so the figure IS the unit. It also
 * means a panel is one request whatever the figure.
 */
/**
 * How many reports the panel draws at a time, and grows by as a reader scrolls.
 *
 * Here rather than in the component because `REPORTS_PER_PAGE` below is a multiple OF it: a page is
 * a buffer measured in screens, and the two numbers only mean anything together. Written out in
 * both places they are a relationship stated in prose, which is the shape this repository has been
 * bitten by often enough to stop doing it.
 *
 * Twenty is roughly a screenful of collapsed reports on a phone — enough that the first scroll has
 * somewhere to go, small enough that opening Muhammad's panel is not 2,600 citations, paragraphs,
 * controls and chains built before a word is read.
 */
export const REPORTS_PER_STEP = 20;

/**
 * How many reports one page of a figure's file holds. See `ARTIFACTS.reports` for why there are pages.
 *
 * FORTY, AND IT WAS TWO HUNDRED — measured 2026-08-22, because two hundred was never measured.
 *
 * This is the first fetch a reader pays after clicking a figure, and the section it fills is a
 * COLLAPSED disclosure: until they open it, the only things on screen are the heading and a count,
 * and both are read from the bundle. Re-cut with the real serializer over the real 1,599 reports of
 * the busiest figure:
 *
 *   page 200   first fetch 444 kB    8 files      page  40   first fetch  77 kB   40 files
 *   page 100   first fetch 200 kB   16 files      page  20   first fetch  40 kB   80 files
 *
 * Total transfer is 2.61 MB either way — this moves bytes off the moment a reader is deciding
 * whether to stay, and nowhere else. Across the library the median first fetch is unmoved (the
 * median figure has 38 reports and fits in any of these); it is the busy narrators that fall, and
 * they are the ones a reader opens.
 *
 * NOT SMALLER, because a page is a buffer measured in screens. `ReportsList` draws twenty at a time
 * and asks for the next page when the drawn count reaches what it holds, so forty is exactly two
 * screens: one to read while the next arrives. Twenty would ask again at the bottom of every screen.
 */
export const REPORTS_PER_PAGE = REPORTS_PER_STEP * 2;

export interface ReportsArtifact {
  /** The gazetteer id whose panel this serves. */
  node: string;
  /**
   * WHICH PAGE THIS IS, AND HOW MANY THERE ARE — added 2026-08-20.
   *
   * A reader meets twenty reports and scrolls for more, so a figure's file is cut into pages of
   * `REPORTS_PER_PAGE` and the panel asks for the next one when it runs out. `pages` is on every
   * page so a client knows after ONE fetch whether there is more, without a count from elsewhere
   * that could disagree with the files on disk.
   *
   * Absent on a file written before this existed, which reads as page 0 of 1 — a single-page
   * figure, which is what most of them are.
   */
  page?: number;
  pages?: number;
  /**
   * HOW MANY OF THIS FIGURE'S REPORTS THEY ONLY PASSED ON — counted over the whole list, added
   * 2026-08-22, and written on every page for the reason `pages` is.
   *
   * The panel discloses that most of a busy narrator's reports are ones they TRANSMITTED rather
   * than ones about them — the difference between "Abu Hurayra said" and "this is about Abu
   * Hurayra". It computed that from the reports it held, and it holds one page: 200 rows, ordered
   * so the ones that NAME the figure lead. So the count was 0 for every busy narrator and the
   * disclosure never rendered, on precisely the figures it exists for. Measured across all eight
   * of his pages: **1,294 of 1,599**, where page 0 alone says none. Aisha 579 of 1,168.
   *
   * It belongs here rather than on `Bundle` because it describes THIS list, and the bundle is
   * loaded by every reader while these files are fetched only when a panel opens.
   *
   * `via === 'narrated'` — the client's own rule, unchanged, so `both` still counts as named.
   * Absent where a figure passed none on, and on a file written before this existed; the panel
   * falls back to counting what it holds, which is what it did for everybody until now.
   */
  narrated?: number;
  reports: {
    ref: string;
    /** Arabic. Always present — see Report.original. */
    ar: string;
    /** The chain, collector first, as names. */
    isnad: string[];
    /**
     * WHO EACH LINK OF THE CHAIN IS, positionally — added 2026-08-19.
     *
     * ═══════════════════════════════════════════════════════════════════════════════════════
     * `CLAUDE.md` opens on why a chain of names is not a chain of people: *"إبراهيم appears in
     * 1,955 Bukhārī reports and 1,128 of those are Ibrahim al-Nakhaʿi in the chain, not the
     * prophet."* A chain gives the shortest name that identified a man to his contemporaries, and
     * a reader a thousand years later gets two words.
     *
     * Where a corpus STATES the identity, this carries it. hadith-kg gives a `rawi_id` per position
     * — 257,437 of this library's links have one — and its register gives that man his full name
     * and his death year. So `isnad[4]` reads **ابن عجلان** and `who[4]` reads *Muḥammad b. ʿAjlān
     * al-Qurashī, d. 148*.
     *
     * **THE NAME IS NOT REPLACED.** The book says what the book says; this sits beside it. `texts
     * are never modified`, and a name silently upgraded to its fullest form is a modified text.
     *
     * POSITIONAL AND SPARSE: same length as `isnad` where present, with an empty string wherever
     * the corpus does not identify that link. Absent entirely on every shelf whose supplier states
     * nothing — Islam Ware, Athar, Wikisource — where the names are all a reader gets.
     *
     * AND IT CARRIES NO GRADING. The register also holds a `rank` for each man — ثقة, متروك
     * الحديث — which is the tradition's verdict on him and names nobody who reached it. That is
     * the same shape as the report-level grading column this library refuses, so it is not here.
     * A name and a date are facts; a rank is somebody's judgement and needs their name on it.
     */
    who?: string[];
    /**
     * THE SAME REPORT IN THE OTHER COLLECTION — muttafaq alayh, agreed upon.
     *
     * The highest standing the tradition gives a report, and the only thing about a hadith's
     * weight this library can establish from the text it holds. It replaces a `grade` field
     * that was never once populated: the source carries no grade because the two Sahihs are
     * ṣaḥīḥ by definition — the collection IS the grade — so the field advertised something
     * the data never had.
     *
     * A fact about two texts, not a claim about anyone, which is what makes it assertable
     * here. Neither collector cross-references the other; "agreed upon" is a later category
     * built by exactly this comparison, so the wording stays "also in", not "graded".
     *
     * AND THAT REASONING NOW COVERS TWO COLLECTIONS OUT OF SIX — noted 2026-08-16.
     *
     * "The collection IS the grade" was a complete account of the data while this shelf held
     * Bukhari and Muslim. The four Sunan added since are canonical and are NOT ṣaḥīḥ throughout:
     * their compilers grade report by report, and all four carry hadith the tradition holds weak.
     * So for two thirds of the reports here the absence of a `grade` field is no longer a
     * justified absence — it is a real gap, and the reason is that the source file carries no
     * grades rather than that grades would be redundant.
     *
     * `alsoIn` itself is unaffected and stays right: it is computed from the Sahihayn alone —
     * see `SAHIHAYN` in `lib/report-parallels.ts`, which had to be made explicit when adding the
     * Sunan silently widened *muttafaq ʿalayh* to mean "appears in two books".
     *
     * What a reader is told instead is `CollectionSpec.standing`, which says of the Sunan that
     * they are graded report by report and that the grades are not in this data. That is the
     * honest limit: naming the collection's character where the individual grade cannot be given.
     */
    alsoIn?: string[];
    /**
     * WHERE THIS REPORT SITS IN ITS COLLECTION — the book, and the chapter where it is known.
     *
     * ═══════════════════════════════════════════════════════════════════════════════════════
     * `corpora/hadith/bukhari.json` addresses a report by number and nothing else, so a reader met
     * the most-read hadith collection in Islam as a flat run of seven thousand items with no way to
     * see that one sits in the Book of Prayer and the next in the Book of Military Expeditions.
     * That is not a small omission: **al-Bukhārī's arrangement is an argument.** He puts a report
     * under a chapter heading he wrote, and the heading is how he says what he thinks it means.
     *
     * The supplier cannot give it — four pipe-separated columns, the first a constant `0` — and no
     * numbering map exists between editions. It is joined by WORDS instead:
     * `pipeline/map-bukhari-structure.ts` locates each report in a named printed edition and reads
     * off the enclosing heading, refusing anything found in more than one place.
     *
     * **`inferred` IS THE HONEST HALF OF THE FIELD.** 76.4% of reports were located outright and
     * carry a `bab`; a further 22.2% sit between two located reports of the SAME book, in a shelf
     * that runs in the book's own order, and take that book without a chapter. The second is an
     * inference from two measurements and the first is a measurement, and a reader is owed the
     * difference rather than a uniform-looking label over both.
     *
     * ONE IN TWO HUNDRED AND FIFTY IS WRONG, and that is measured rather than hoped: the shelf runs
     * 1…7,008 in the book's order, so a correct map climbs, and 20 of 5,250 located reports sit
     * after a higher book number. Absent beats wrong, so nothing is guessed — but this is not zero
     * and the page should not imply it is.
     */
    place?: {
      /**
       * The book number, as the edition's own table of contents numbers it.
       *
       * ABSENT ON A MUSNAD, and that is the collection rather than a gap: the Musnad of Aḥmad is
       * arranged by NARRATOR — «مسند العشرة المبشرين بالجنة» — so it has no numbered books at all.
       * Its reports carry a `name` and no number, which is the structure that collection has.
       */
      kitab?: number;
      /** The book's title — `كتاب التفسير` — or, on a musnad, whose musnad it is. */
      name: string;
      /** The chapter's title, where the report was located outright. */
      bab?: string;
      /**
       * The same two titles in English, where the source gives them.
       *
       * PRESENT ON THE TWELVER SHELF AND ABSENT ON THE SUNNI ONES, and that asymmetry is the
       * sources rather than a preference: the Sunni structure is read off Arabic printings, while
       * ThaqalaynData's index carries an English for every book and chapter it lists. So a reader
       * without Arabic gets more of the Twelver shelf's shape than of Bukhārī's — the reverse of
       * what this library's balance usually looks like, and worth not hiding.
       */
      nameEn?: string;
      babEn?: string;
      /** Set where the book comes from the reports either side rather than from this one. */
      inferred?: true;
    };
    /**
     * Ayahs this report quotes, resolved from the text — "Quran 2:143".
     *
     * The one cross-corpus link that needs no identity claim: the edition braces its Quranic
     * quotations, and a match is a fact about two strings rather than an assertion about who
     * someone is. A reader can follow it to a verse the library already holds and read the
     * two side by side, which is what this project is for.
     */
    cites?: string[];
    /**
     * THE EARLIER WORK THIS REPORT WAS QUOTED FROM — 2026-08-19.
     *
     * ═══════════════════════════════════════════════════════════════════════════════════════
     * A SUPPLEMENT'S WHOLE APPARATUS, CARRIED IN THE DATA AND SHOWN TO NOBODY. `lib/mustadrak.ts`
     * extracts `citedFrom` and has tests pinning four shapes of it; `Report.citedFrom` declares
     * it; `corpora/mustadrak.json` carries it on **21,946 of 22,351 rows** — and no stage between
     * there and a reader ever named it.
     *
     * It matters most on the shelf it comes from. Mustadrak al-Wasāʾil is a nineteenth-century
     * SUPPLEMENT: al-Nūrī collected what the Wasāʾil left out, and every report in it is quoted
     * from somewhere older. Without this field a reader sees a report attributed to a book of
     * 1902 and no sign that its content is centuries earlier — which is the opposite of what this
     * library is for.
     *
     * The three largest are `دعائم الإسلام` at 2,659, `فقه الرضا` at 1,403 and `الجعفريات` at
     * 1,335 — and the first of those is the Ismaili work `absent.ts` records as unobtainable.
     *
     * **VERBATIM, INCLUDING THE PARTS THAT NAME NO BOOK.** 649 rows read `بهذا الإسناد` — "by this
     * chain", meaning the report before — and others name a person rather than a work. Those are
     * what the edition says, and normalising them into "unknown" would delete the distinction
     * between a report whose source is stated and one whose source is the previous entry.
     */
    citedFrom?: string;
    /**
     * WHICH BRANCH HOLDS THE COLLECTION THIS REPORT STANDS IN — 2026-08-19.
     *
     * ═══════════════════════════════════════════════════════════════════════════════════════
     * A FIGURE'S PANEL MIXES SHELVES AND SAID SO NOWHERE. `Bukhari 4662`, `mustadrak 1:228` and
     * `musnad-zayd 12` sit one under another — Sunni, Twelver and Zaydi — identified only by a ref
     * a reader has to already know how to read. The Sources panel gives each shelf its `heldBy` and
     * `notHeldBy`, which is another panel and another click.
     *
     * A KEY, NOT A WORD. `sunni`, `twelver`, `zaydi`, `ibadi` — the page is English, Hebrew and
     * Arabic, and shipping "Twelver Shia" in the artifact would put an English noun in front of a
     * reader who chose Arabic.
     *
     * ABSENT RATHER THAN GUESSED where `lib/report-shelves.ts` does not place the collection. A
     * report with no label reads as "this was not said"; a wrongly-labelled one tells a Zaydi
     * reader their collection is Sunni, which is the failure `roster.ts` exists to prevent.
     */
    branch?: 'sunni' | 'twelver' | 'zaydi' | 'ibadi';
    /**
     * THE COMPILER'S OWN VERDICT ON THIS REPORT, AND THE MAN WHO GAVE IT — 2026-08-19.
     *
     * ═══════════════════════════════════════════════════════════════════════════════════════
     * `alsoIn` above says the grade field "was never once populated" and calls its absence "a
     * real gap… because the source file carries no grades". **A source that does was found.**
     * al-Ḥākim assesses his reports himself, report by report, inside the text this library
     * already carries: 4,495 of his 8,907 carry a sentence — more than al-Tirmidhī's 2,882,
     * whose 3,891 are also here and whose verdicts `lib/compiler-verdict.ts` can already read.
     *
     * NEVER WITHOUT `gradedBy`, WHICH IS WHY THE TWO ARE DECLARED TOGETHER. `reportProblems`
     * refuses one without the other, and the case is not hypothetical: hadith-kg ships a second
     * grading column for the same book that contradicts al-Ḥākim on 1,212 of these reports and
     * names no author, so shown bare it would read as his — or as this library's. A verdict is
     * one scholar's opinion of another's report and is worth nothing detached from him.
     *
     * `gradeText` is the sentence itself. "ṣaḥīḥ" is this library's reading of *"this is a sound
     * hadith on the criteria of the two Shaykhs, for ʿAbd Allāh b. Nāfiʿ is trustworthy"*, and a
     * reader who wants the difference should be able to see it rather than take the label.
     */
    grade?: Grade;
    /** Who gave the verdict, named. Present whenever `grade` is. */
    gradedBy?: string;
    /** The verdict in his own words, which the classification is a reading of. */
    gradeText?: string;
    /**
     * THE OTHER FIGURES THIS REPORT NAMES — the way out of a page with no translation.
     *
     * A reader who cannot read Arabic gets a citation, a chain and a wall of script. The one
     * thing that makes it navigable is knowing who else is in it: a report on Abraham's panel
     * that also names Ishmael, Mecca and the Kaaba is a report about the building of the
     * Kaaba, and that much is legible without a word of Arabic.
     *
     * NOT RE-DERIVED IN THE BROWSER. This is `graph/report-mentions.json` inverted — the same
     * index the panel was opened from, read the other way — so the page can only link what
     * the build already claims, and a link here can never disagree with the count beside it.
     *
     * The matn only. A chain link resolves for a curated Companion because the position is
     * the evidence, but a name in the chain is the person who PASSED the report on, not
     * someone the report is about, and offering it as "also named" would say the wrong thing.
     */
    also?: { id: string; name: string }[];
    /**
     * HOW THIS FIGURE IS IN THIS REPORT — and the two are not the same claim.
     *
     * `named` — the report's own text names them. `narrated` — they are a link in its chain,
     * so they passed it on; the report may be about someone else entirely, or about nobody.
     *
     * The panel's heading is "Also related about X", and for Abu Hurayra 1,374 of 1,825 are
     * reports he TRANSMITTED. Presenting those as things related about him says something the
     * data does not: he is the most prolific narrator in the collection, which is a fact about
     * the apparatus rather than about his life. Both belong on his panel — his node exists
     * BECAUSE of that position — but a reader has to be able to tell which they are reading.
     *
     * Absent means `named`, which is the ordinary case and every prophet's whole list.
     */
    via?: 'narrated' | 'both';
  }[];
}

export function reportsJson(a: ReportsArtifact): string {
  const node = JSON.stringify(a.node);
  const reports = JSON.stringify(a.reports);
  /* Omitted for a single-page figure, which is most of them: the fields exist to say "there is
     more", and writing `"pages":1` on 140 files is bytes that say nothing. */
  const paging = (a.pages ?? 1) > 1 ? `,"page":${a.page ?? 0},"pages":${a.pages}` : '';
  /* Omitted at zero for the same reason paging is at one: a figure who passed none on has nothing
     to disclose, and the panel's fallback reads an absent field exactly that way. */
  const narrated = (a.narrated ?? 0) > 0 ? `,"narrated":${a.narrated}` : '';
  return `{"node":${node}${paging}${narrated},"reports":${reports}}\n`;
}

export function searchIndexJson(a: SearchIndexArtifact): string {
  const corpus = JSON.stringify(a.corpus);
  const index = JSON.stringify(a.index);
  const df = JSON.stringify(a.df);
  return `{"corpus":${corpus},"index":${index},"df":${df}}\n`;
}

export function nodeRefsJson(a: NodeRefsArtifact): string {
  const corpus = JSON.stringify(a.corpus);
  const refs = JSON.stringify(a.refs);
  return `{"corpus":${corpus},"refs":${refs}}\n`;
}

/**
 * THE WASHES AND THE WHEEL TRAVEL WITH IT, and for a while they did not.
 *
 * `buildSkeleton` has always returned all four, and the interface has always declared all four —
 * this writer serialised three. So the chunked map (D26) reached the browser with no scripture
 * washes and no wedges at all, and `App.tsx` fell back to `{}` and `[]` without complaint, which is
 * exactly the drift the interface's own comment warns about: a layer that appears on one door and
 * not the other. Two doors to the same map, and a renderer must not be able to tell which one it
 * came through.
 */
export function mapSkeletonJson(a: MapSkeletonArtifact): string {
  const corpora = JSON.stringify(a.corpora);
  const rows = JSON.stringify(a.rows);
  const edges = JSON.stringify(a.edges);
  const fields = JSON.stringify(a.fields);
  const wheel = JSON.stringify(a.wheel);
  return `{"corpora":${corpora},"rows":${rows},"edges":${edges},"fields":${fields},"wheel":${wheel}}\n`;
}

export function mapTilesJson(a: MapTilesArtifact): string {
  const corpora = JSON.stringify(a.corpora);
  const tiles = JSON.stringify(a.tiles);
  return `{"corpora":${corpora},"count":${a.count},"rim":${a.rim},"tile":${a.tile},"tiles":${tiles}}\n`;
}

export function mapTileJson(a: MapTileArtifact): string {
  const id = JSON.stringify(a.id);
  const n = JSON.stringify(a.n);
  const e = JSON.stringify(a.e);
  return `{"id":${id},"n":${n},"e":${e}}\n`;
}

export function mapOverviewJson(a: MapOverviewArtifact): string {
  const corpora = JSON.stringify(a.corpora);
  const cells = JSON.stringify(a.cells);
  const links = JSON.stringify(a.links);
  return `{"corpora":${corpora},"cells":${cells},"links":${links}}\n`;
}

export function nodeRecordJson(a: NodeRecordArtifact): string {
  const node = JSON.stringify(a.node);
  const ties = JSON.stringify(a.ties);
  return `{"node":${node},"ties":${ties}}\n`;
}

/**
 * The lists, in a pinned key order like every artifact above.
 *
 * An ABSENT field is written as absent rather than as an empty array, which is the one place this
 * differs from the others and is deliberate: `pipeline/lib/derived-layer.ts` distinguishes a field that
 * is gone (a producer stopped emitting it — always worth stopping for) from one that is present and
 * empty (which may be perfectly true). Writing `[]` for something never built would erase that
 * distinction at the one moment it matters.
 */
export function compilationListsJson(a: CompilationListsArtifact): string {
  const parts: string[] = [];
  const put = (key: keyof CompilationListsArtifact): void => {
    const value = a[key];
    if (value !== undefined) parts.push(`${JSON.stringify(key)}:${JSON.stringify(value)}`);
  };
  put('contestedPassages');
  put('passageDisputes');
  put('contested');
  put('stories');
  put('divergences');
  put('internalParallels');
  put('internal');
  put('textualNotes');
  put('identities');
  put('reportCounts');
  return `{${parts.join(',')}}\n`;
}

/** Where each artifact lives, relative to the artifact root — named once so the writer and
    the reader cannot drift apart. */
export const ARTIFACTS = {
  bundle: (): string => 'bundle.json',
  verseChunk: (chunk: number): string => `verses/v${chunk}.json`,
  searchIndex: (corpus: string): string => `index/${corpus}.json`,
  nodeRefs: (corpus: string): string => `refs/${corpus}.json`,
  /* A node id reaches this from a URL segment, so it is constrained by isArtifactId for the
     same reason a corpus id is — see the note above. Gazetteer ids carry hyphens, which the
     pattern already allows. */
  /**
   * A figure's reports, in PAGES — page 0 keeps the original path so nothing that fetched it
   * before has to change.
   *
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * WHY IT IS PAGED NOW AND WAS NOT BEFORE. The file per figure was right at 12,370 reports and
   * broke at 151,363: **`abu-hurayra.json` reached 19.2 MB** on 2026-08-20, and prayer 19.0,
   * ʿĀʾisha 16.7 — 312 MB across 145 files, after eight books from one supplier added 49,759
   * reports in a day. `ReportsArtifact`'s own header argues reports stay out of the bundle
   * because *"a reader who never opens a figure should not download it"*; a reader who DOES open
   * one should not download nineteen megabytes either.
   *
   * And it grows linearly with every book, with 48 collections enumerated and waiting.
   */
  reports: (node: string, page = 0): string =>
    (page > 0 ? `reports/${node}.p${page}.json` : `reports/${node}.json`),

  /**
   * THE INVERSE, so nothing has to spell the naming out a second time.
   *
   * `qa-artifacts` read the node id straight off the filename, which was right until pagination
   * arrived on 2026-08-20 and made `abu-hurayra.p2.json` a file about a figure called
   * "abu-hurayra.p2" — a figure not on the map. It reported that 68 times and failed the build,
   * for 41 files that were perfectly correct.
   *
   * A rule for writing a name and a rule for reading it are the same rule. Kept next to the writer
   * above so they cannot drift again; a figure whose own id ends in `.p3` would defeat this, and no
   * gazetteer id contains a dot at all.
   */
  reportNode: (file: string): string =>
    file.replace(/^reports\//, '').replace(/\.json$/, '').replace(/\.p\d+$/, ''),

  /* The compilation (D26). Every one of these is under COMPILATION_PREFIX so that the object
     store can be told once that the prefix is not world-readable — see DELIVERY.md §12, where
     going private and removing the layer from the bundle are the same commit. */
  mapSkeleton: (): string => `${COMPILATION_PREFIX}/map/skeleton.json`,
  mapOverview: (): string => `${COMPILATION_PREFIX}/map/overview.json`,
  mapTiles: (): string => `${COMPILATION_PREFIX}/map/tiles/index.json`,
  /* A tile id is `<X>_<Y>` and is built from the CAMERA rather than from a URL segment — but it
     still reaches a path, so it is held to the same rule as everything else here. */
  mapTile: (id: string): string => `${COMPILATION_PREFIX}/map/tiles/c${id}.json`,
  compilationLists: (): string => `${COMPILATION_PREFIX}/lists.json`,
  /* A node id reaches this from a URL segment; see `reports` above, and the guard below. */
  nodeRecord: (node: string): string => `${COMPILATION_PREFIX}/node/${node}.json`,
} as const;

/**
 * Whether a name is safe to put in a compilation path.
 *
 * The same argument as `isArtifactId`, which this defers to: these paths are built from URL
 * segments, so the id is attacker-controlled by definition, and containment alone lets `../bundle`
 * answer for a node record. A caller that has not checked is a caller reading somebody else's file.
 */
export function isCompilationId(id: string): boolean {
  return isArtifactId(id);
}
